import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Spinner, Badge } from 'react-bootstrap';
import { RiFileExcel2Line } from "react-icons/ri";
import { FaSync } from 'react-icons/fa';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/VehicleTable.css';
import markericon from '../images/car.png';
import { GetFromApi } from '../utils/api';
import socketService from '../utils/socket';

const VehicleTable = ({ data, vehicles = [], refreshData }) => {
  const [tableData, setTableData] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mapCenter, setMapCenter] = useState([23.0225, 72.5714]);
  const [isLoading, setIsLoading] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Fetch vehicle data from API
  const fetchVehicleData = async () => {
    try {
      setIsLoading(true);
      const response = await GetFromApi('/dashboard/vehicles');
      if (response.success) {
        const formattedData = response.data.map(v => ({
          id: v.id,
          vehicle: v.number,
          number: v.number,
          date: new Date().toLocaleDateString(),
          dateTime: v.lastUpdate ? new Date(v.lastUpdate).toLocaleString() : new Date().toLocaleString(),
          location: v.address || 'Fetching location...',
          odometer: v.odometer || 0,
          speed: v.speed || 0,
          ignition: v.ignition || false,
          status: v.status,
          pos: v.lat && v.lng ? 'Fixed' : 'Unknown',
          igntime: v.ignition ? 'ON' : 'OFF',
          network: 'Online',
          gps: 'Fixed',
          intVolt: v.int_voltage ? `${parseFloat(v.int_voltage).toFixed(1)}V` : '0.0V',
          extVolt: v.ext_voltage ? `${parseFloat(v.ext_voltage).toFixed(1)}V` : '0.0V',
          distance: `${parseFloat(v.odometer || 0).toFixed(1)} Km`,
          lat: v.lat,
          lng: v.lng,
          heading: v.heading || 0,
          driver_name: v.driver_name
        }));
        setTableData(formattedData);
      }
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Use prop data or fetch from API
  useEffect(() => {
    if (vehicles.length > 0 || (data && data.length > 0)) {
      const sourceData = vehicles.length > 0 ? vehicles : data;
      setTableData(sourceData.map(v => ({
        ...v,
        vehicle: v.vehicle || v.number,
        date: v.date || new Date().toLocaleDateString(),
        dateTime: v.dateTime || new Date().toLocaleString(),
        location: v.location || v.address || 'Unknown',
        odometer: v.odometer || 0,
        speed: v.speed || 0,
        ignition: v.ignition || false,
        pos: v.lat && v.lng ? 'Fixed' : 'Unknown',
        igntime: v.ignition ? 'ON' : 'OFF',
        network: 'Online',
        gps: 'Fixed',
        intVolt: v.int_voltage ? `${parseFloat(v.int_voltage).toFixed(1)}V` : (v.intVolt || '0.0V'),
        extVolt: v.ext_voltage ? `${parseFloat(v.ext_voltage).toFixed(1)}V` : (v.extVolt || '0.0V'),
        distance: `${parseFloat(v.odometer || 0).toFixed(1)} Km`
      })));
    } else {
      fetchVehicleData();
    }
  }, [vehicles, data]);

  // Socket.IO real-time updates
  useEffect(() => {
    socketService.connect();
    
    socketService.on('vehicle:location', (locationData) => {
      setTableData(prev => prev.map(v => {
        if (v.id === locationData.vehicleId) {
          return {
            ...v,
            speed: locationData.speed,
            lat: locationData.lat,
            lng: locationData.lng,
            heading: locationData.heading,
            status: locationData.isMoving ? 'running' : 'stopped',
            dateTime: new Date().toLocaleString(),
            ignition: locationData.ignition !== undefined ? locationData.ignition : v.ignition,
            igntime: (locationData.ignition !== undefined ? locationData.ignition : v.ignition) ? 'ON' : 'OFF'
          };
        }
        return v;
      }));

      // Update modal map marker if open for this vehicle
      if (selectedVehicle && selectedVehicle.id === locationData.vehicleId && markerRef.current) {
        const newPos = [locationData.lat, locationData.lng];
        animateMarkerTo(markerRef.current, newPos);
      }
    });

    return () => {
      socketService.off('vehicle:location');
    };
  }, [selectedVehicle]);

  // Countdown timer disabled - we rely on WebSockets for real-time updates
  useEffect(() => {
    // Auto-refresh is disabled to prevent constant API calls.
  }, []);

  // Smooth marker animation
  const animateMarkerTo = (marker, newPosition, duration = 1000) => {
    const startPos = marker.getLatLng();
    const startLat = startPos.lat;
    const startLng = startPos.lng;
    const deltaLat = newPosition[0] - startLat;
    const deltaLng = newPosition[1] - startLng;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      marker.setLatLng([
        startLat + deltaLat * easeOut,
        startLng + deltaLng * easeOut
      ]);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  // Initialize map
  const initMap = () => {
    if (!selectedVehicle || !L) return;

    if (mapRef.current) {
      mapRef.current.remove();
    }

    const mapContainer = document.getElementById('vehicle-map');
    if (!mapContainer) return;

    const lat = parseFloat(selectedVehicle.lat) || mapCenter[0];
    const lng = parseFloat(selectedVehicle.lng) || mapCenter[1];

    const map = L.map('vehicle-map').setView([lat, lng], 15);
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    const customIcon = L.divIcon({
      className: 'custom-vehicle-marker',
      html: `
        <div style="position: relative; transform: rotate(${selectedVehicle.heading || 0}deg); transition: transform 0.5s;">
          <img src="${markericon}" style="width: 40px; height: 40px;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });

    const marker = L.marker([lat, lng], { icon: customIcon }).addTo(map);
    markerRef.current = marker;

    marker.bindPopup(`
      <div style="min-width: 200px;">
        <h6 style="margin: 0 0 10px; padding-bottom: 5px; border-bottom: 2px solid #3B82F6;">
          ${selectedVehicle.vehicle || selectedVehicle.number}
        </h6>
        <p style="margin: 5px 0;"><strong>Speed:</strong> ${selectedVehicle.speed || 0} km/h</p>
        <p style="margin: 5px 0;"><strong>Location:</strong> ${selectedVehicle.location}</p>
        <p style="margin: 5px 0;"><strong>Driver:</strong> ${selectedVehicle.driver_name || 'N/A'}</p>
        <p style="margin: 5px 0;"><strong>Status:</strong> 
          <span style="color: ${selectedVehicle.speed > 0 ? '#22C55E' : '#EF4444'}">
            ${selectedVehicle.speed > 0 ? 'Running' : 'Stopped'}
          </span>
        </p>
        <p style="margin: 5px 0;"><strong>Last Update:</strong> ${selectedVehicle.dateTime}</p>
      </div>
    `, { maxWidth: 300 }).openPopup();
  };

  useEffect(() => {
    if (showModal && selectedVehicle) {
      setTimeout(initMap, 300);
    }
  }, [showModal, selectedVehicle]);



  const handleMapClick = (item) => {
    setSelectedVehicle(item);
    setMapCenter([parseFloat(item.lat) || 23.0225, parseFloat(item.lng) || 72.5714]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    markerRef.current = null;
  };

  const handleManualRefresh = () => {
    if (refreshData) refreshData();
    else fetchVehicleData();
  };

  const getStatusBadge = (item) => {
    const isRunning = item.status === 'running' || item.status === 'active' || item.speed > 3 || item.ignition;
    return (
      <Badge bg={isRunning ? 'success' : 'danger'} style={{ fontSize: '11px' }}>
        {isRunning ? 'Running' : 'Stopped'}
      </Badge>
    );
  };

  return (
    <div className="table-container">
      <div className="header-row">
        <div>
          <h2 className='vname'>Vehicle Information Summary</h2>
          <p>
            Real-time updates active (WebSockets)
            <Button 
              variant="link" 
              size="sm" 
              onClick={handleManualRefresh}
              disabled={isLoading}
              style={{ marginLeft: '10px' }}
            >
              {isLoading ? <Spinner size="sm" /> : <FaSync />}
            </Button>
          </p>
        </div>
        <Button variant="custom" className="export-btn">
          <RiFileExcel2Line /> Export to Excel
        </Button>
      </div>

      {isLoading && tableData.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading vehicle data...</p>
        </div>
      ) : (
        <Table bordered hover responsive>
          <thead>
            <tr>
              <th>Sr.</th>
              <th>Date</th>
              <th>Date & Time</th>
              <th>Vehicle</th>
              <th>Location</th>
              <th>Odometer</th>
              <th>Speed</th>
              <th>Ignition</th>
              <th>Status</th>
              <th>GPS</th>
              <th>Int. V</th>
              <th>Ext. V</th>
              <th>Map</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((item, index) => (
              <tr key={item.id || index} style={{ 
                backgroundColor: item.speed > 0 ? 'rgba(34, 197, 94, 0.05)' : 'transparent'
              }}>
                <td>{index + 1}</td>
                <td>{item.date}</td>
                <td>{item.dateTime}</td>
                <td><strong>{item.vehicle || item.number}</strong></td>
                <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.location}
                </td>
                <td>{item.odometer} km</td>
                <td>
                  <span style={{ color: item.speed > 0 ? '#22C55E' : '#6B7280', fontWeight: 'bold' }}>
                    {item.speed} km/h
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span className={`circlev ${item.ignition ? 'on' : 'off'}`}>
                    <span className="inner-dot"></span>
                  </span>
                </td>
                <td>{getStatusBadge(item)}</td>
                <td>{item.gps || 'Fixed'}</td>
                <td>{item.intVolt}</td>
                <td>{item.extVolt}</td>
                <td>
                  <Button
                    size="sm"
                    className="map-btn"
                    variant="primary"
                    onClick={() => handleMapClick(item)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {tableData.length === 0 && !isLoading && (
        <div className="text-center py-5 text-muted">
          <p>No vehicle data available</p>
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Live Location - {selectedVehicle?.vehicle || selectedVehicle?.number}
            {selectedVehicle?.speed > 0 && (
              <Badge bg="success" className="ms-2">Running</Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div style={{ height: '450px', width: '100%' }}>
            <div id="vehicle-map" style={{ height: '100%', width: '100%' }}></div>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #eee' }}>
            <p style={{ margin: 0, textAlign: 'center', color: '#666' }}>
              <strong>Real-time updates enabled</strong> - Marker will move automatically as vehicle position updates
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default VehicleTable;
